// 게임 환경설정 훅 — localStorage 즉시 저장 + 로그인 유저 DB 동기화
import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@contexts/AuthContext';
import gameApi from '@api/game';
import { toast } from '@ui/overlay/toastStore';
import { DEVICE_CAPS, DEVICE_PROFILE } from '@/render/deviceProfile';

import { GAME_SETTINGS_KEY } from '@/config/storageKeys';
import { getRaw, removeKey, saveJSON } from '@/lib/persist';
import { GAME_SETTINGS_SCHEMA, RETIRED_SETTING_KEYS, defaultsOf, deviceLocalKeysOf } from '@/config/settingsSchema';

export { DEVICE_PROFILE };

const STORAGE_KEY = GAME_SETTINGS_KEY;   // 문자열의 집은 `config/storageKeys.js` 하나다

// HDRI preset → 로컬 정적 파일 + 시간대 메타 (front/public/hdri/, SW 영구 캐시 대상)
// fantasy_*: AI 생성 스타일라이즈드 스카이박스. 나머지: CC0(Polyhaven). 출처는 front/public/hdri/LICENSE.txt 참조.
// time: 'day' | 'dawn' | 'dusk' | 'night' — 하늘 외관과 조명을 함께 결정(항상 일치).
export const HDRI_PRESETS = {
  fantasy_dusk:     { url: '/hdri/fantasy_dusk_2k.hdr',     thumb: '/hdri/thumbs/fantasy_dusk.png',     label: '판타지 불구름 노을', time: 'dusk'  },
  fantasy_twilight: { url: '/hdri/fantasy_twilight_2k.hdr', thumb: '/hdri/thumbs/fantasy_twilight.png', label: '판타지 몽환 황혼',   time: 'dusk'  },
  fantasy_night:    { url: '/hdri/fantasy_night_2k.hdr',    thumb: '/hdri/thumbs/fantasy_night.png',    label: '판타지 성운의 밤',   time: 'night' },
  day_clear:   { url: '/hdri/day_clear_2k.hdr',   thumb: '/hdri/thumbs/day_clear.png',   label: '쾌청한 한낮', time: 'day'   },
  day_clouds:  { url: '/hdri/day_clouds_2k.hdr',  thumb: '/hdri/thumbs/day_clouds.png',  label: '구름 낀 낮',  time: 'day'   },
  dawn:        { url: '/hdri/dawn_2k.hdr',        thumb: '/hdri/thumbs/dawn.png',        label: '물안개 새벽', time: 'dawn'  },
  dusk:        { url: '/hdri/dusk_2k.hdr',        thumb: '/hdri/thumbs/dusk.png',        label: '불타는 노을', time: 'dusk'  },
  night_stars: { url: '/hdri/night_stars_2k.hdr', thumb: '/hdri/thumbs/night_stars.png', label: '은하수 밤',   time: 'night' },
  night_moon:  { url: '/hdri/night_moon_2k.hdr',  thumb: '/hdri/thumbs/night_moon.png',  label: '푸른 달밤',   time: 'night' },
};

// 시간대 메타 — UI 시간 버튼 + 조명 프로필 공용 키
export const TIME_OF_DAY = {
  day:   { label: '낮',   icon: '☀', night: false },
  dawn:  { label: '새벽', icon: '◔', night: false },
  dusk:  { label: '황혼', icon: '◑', night: false },
  night: { label: '밤',   icon: '☾', night: true  },
};
export const TIME_ORDER = ['day', 'dawn', 'dusk', 'night'];

// 시간대별 보조 조명 프로필 — HDRI(IBL)에 더해줄 ambient/directional + 환경맵 세기.
export const LIGHTING_PROFILE = {
  day:   { ambIntensity: 1.2,  ambColor: '#fff3e2', dirIntensity: 1.1,  dirColor: '#ffe9cf', envIntensity: 1.0 },
  dawn:  { ambIntensity: 0.9,  ambColor: '#ffe6d2', dirIntensity: 0.95, dirColor: '#ffd9b0', envIntensity: 1.0 },
  dusk:  { ambIntensity: 0.8,  ambColor: '#ffd2b0', dirIntensity: 0.85, dirColor: '#ff9d6b', envIntensity: 1.0 },
  night: { ambIntensity: 0.2,  ambColor: '#2e3c5e', dirIntensity: 0.28, dirColor: '#9db0dc', envIntensity: 0.7 },
};

// 구버전 hdriPreset 키 → 신규 키 (기존 유저 localStorage/DB 호환)
const LEGACY_PRESET_MAP = { sky: 'day_clouds', sunset: 'dusk', night: 'night_stars' };

function resolveHdriPreset(key) {
  if (HDRI_PRESETS[key]) return key;
  if (LEGACY_PRESET_MAP[key]) return LEGACY_PRESET_MAP[key];
  return 'day_clear';
}
export function getHdriInfo(key) {
  return HDRI_PRESETS[resolveHdriPreset(key)];
}
export function getHdriTime(key) {
  return getHdriInfo(key).time;
}
export function isNightPreset(key) {
  return TIME_OF_DAY[getHdriTime(key)]?.night === true;
}

// 키·기본값·기기 로컬 여부·화면 메타의 선언은 config/settingsSchema.js 한 장이다.
// 기기마다 다른 기본값은 아래 Object.assign(DEVICE_CAPS) 한 줄이 덮는다.
export const DEFAULT_SETTINGS = defaultsOf(GAME_SETTINGS_SCHEMA);

// ── 기기 기본값 — **여기가 유일한 주입점이다** ────────────────────────────
// 프리셋(자동/커스텀/PC쾌적…)은 폐기했다. 그 층이 하던 일은 「저장된 값을 소비 시점에
// 기기 값으로 덮는 것」이었는데, 그래서 «저장했는데 안 먹는» 상태가 생겼다(설정 화면이
// 보여 주는 값과 실제로 도는 값이 갈렸다).
// 지금 규약은 하나다 — **기본값만 기기가 정하고, 저장된 값은 언제나 이긴다.**
// 폰이 처음 켤 때 PC 값을 받지 않게 하는 일은 이 한 줄이 다 한다.
Object.assign(DEFAULT_SETTINGS, DEVICE_CAPS);

// ── 빛 프리셋 ────────────────────────────────────────────────
// 룩을 만드는 손잡이가 9개(톤매핑·노출·햇빛·환경광·대비·그늘색·그림자·그늘진하기·블룸)인데
// 아트 디렉션은 그 9개의 **특정 조합**이라, 조합에 이름을 붙여 준다.
//   natural 실사 자연광 · film 시네마틱 · golden 황혼 · vivid 스크린샷용(형광 허용)
//   storybook 동화풍 — **DEFAULT_SETTINGS 의 빛·색 기본값과 글자 그대로 같아야 한다**
// 그림자 계열은 비용이 있으므로 프리셋이 켜기만 하고 거리는 안 건드린다(거리는 기기 사정이다).
export const LIGHT_PRESETS = {
  natural: {
    label: '자연광', desc: '실사 자연광 · 채도 낮고 눈이 편함',
    toneMapping: true, toneExposure: 1.0, sunIntensity: 1.05, ambientLift: 0.95,
    lightContrast: 0.35, shadeTint: 0.50, shadowStrength: 0.55,
    formLight: 0.55, macroVar: 0.45,
    bloomEnabled: true, bloomThreshold: 0.88, bloomIntensity: 0.55,
    gradeEnabled: true, gradeSplit: 0.55, gradeContrast: 0.22, gradeSat: 0.0,
    gradeVibrance: 0.18, gradeGreenTame: 0.35, gradeHighlight: 0.26,
  },
  film: {
    label: '영화풍', desc: '대비 세움 · 그늘이 푸름 · 등불이 번짐',
    toneMapping: true, toneExposure: 1.05, sunIntensity: 1.0, ambientLift: 0.90,
    lightContrast: 0.50, shadeTint: 0.62, shadowStrength: 0.70,
    formLight: 0.65, macroVar: 0.50,
    bloomEnabled: true, bloomThreshold: 0.72, bloomIntensity: 0.75,
    gradeEnabled: true, gradeSplit: 1.05, gradeContrast: 0.38, gradeSat: 0.10,
    gradeVibrance: 0.22, gradeGreenTame: 0.30, gradeHighlight: 0.32,
  },
  storybook: {
    label: '동화풍', desc: '마크·하이테일 계열 · 색은 있되 형광은 아님',
    toneMapping: true, toneExposure: 1.02, sunIntensity: 1.30, ambientLift: 0.68,
    lightContrast: 0.60, shadeTint: 0.55, shadowStrength: 0.68,
    formLight: 0.60, macroVar: 0.60,
    bloomEnabled: true, bloomThreshold: 0.92, bloomIntensity: 0.50,
    gradeEnabled: true, gradeSplit: 0.85, gradeContrast: 0.30, gradeSat: 0.06,
    gradeVibrance: 0.34, gradeGreenTame: 0.40, gradeHighlight: 0.16,
  },
  golden: {
    label: '황혼', desc: '따뜻하고 하이라이트가 큼 · 해 낮은 시간대와 짝',
    toneMapping: true, toneExposure: 1.12, sunIntensity: 1.15, ambientLift: 0.88,
    lightContrast: 0.55, shadeTint: 0.70, shadowStrength: 0.72,
    formLight: 0.70, macroVar: 0.50,
    bloomEnabled: true, bloomThreshold: 0.66, bloomIntensity: 0.95,
    gradeEnabled: true, gradeSplit: 1.45, gradeContrast: 0.34, gradeSat: 0.08,
    gradeVibrance: 0.26, gradeGreenTame: 0.35, gradeHighlight: 0.45,
  },
  vivid: {
    label: '쨍하게', desc: '밝고 채도 높음 · 스크린샷용(형광을 허용한다)',
    toneMapping: true, toneExposure: 1.15, sunIntensity: 1.20, ambientLift: 1.0,
    lightContrast: 0.35, shadeTint: 0.40, shadowStrength: 0.55,
    formLight: 0.50, macroVar: 0.40,
    bloomEnabled: true, bloomThreshold: 0.82, bloomIntensity: 0.90,
    gradeEnabled: true, gradeSplit: 0.60, gradeContrast: 0.26, gradeSat: 0.30,
    gradeVibrance: 0.40, gradeGreenTame: 0.20, gradeHighlight: 0.30,
  },
};

// ── 렌더링 팩 — 마크 셰이더팩처럼 «룩 + 품질»을 한 벌로 고른다 ───────────────
// LIGHT_PRESETS 는 비용 0 인 룩 키만 바꾸고, 팩은 그 위에 그림자 거리·접지 그늘(SSAO)·지형 그림자·
// 물 품질까지 같이 정한다. 썸네일은 `/ui/renderpack/<키>.webp`(Codex 예시 아틀라스를 자른 것).
// ⚠ ssao·terrainCastShadow·shadowDistance 는 기기 로컬 키다 — 팩을 고른 기기에만 먹는다.
// `sky` 는 설정 키가 아니라 skyStore(시간·구름)에 쓴다 — at: noon | golden(일몰 0.9시간 전) | morning(일출 1.6시간 뒤) | late(10시).
//   빛 키만으로는 팩끼리 화면 밝기 차가 0.04 뿐이었다(예시 그림은 0.30) — 해 높이가 룩의 대부분이다.
const LOOK_HIGH = { shadowsEnabled: true, ssaoEnabled: true, terrainCastShadow: true, waterQuality: 3 };
export const RENDER_PACKS = {
  clear: {
    label: '청명', sky: { at: 'noon', cloud: 0.25 }, fogEnabled: true, fogAutoFar: true, fogNear: 350, ...LOOK_HIGH, shadowDistance: 240, vignetteEnabled: false, cloudShadowEnabled: true,
    toneMapping: true, toneExposure: 1.05, sunIntensity: 1.40, ambientLift: 0.55,
    lightContrast: 0.65, shadeTint: 0.70, shadowStrength: 0.72, formLight: 0.65, macroVar: 0.55,
    bloomEnabled: true, bloomThreshold: 0.92, bloomIntensity: 0.45,
    gradeEnabled: true, gradeSplit: 0.90, gradeContrast: 0.32, gradeSat: 0.0,
    gradeVibrance: 0.28, gradeGreenTame: 0.45, gradeHighlight: 0.18,
  },
  golden: {
    label: '노을', sky: { at: 'golden', cloud: 0.35 }, fogEnabled: true, fogAutoFar: false, fogNear: 180, fogFar: 1400, ...LOOK_HIGH, shadowDistance: 240, vignetteEnabled: true, cloudShadowEnabled: true,
    toneMapping: true, toneExposure: 1.12, sunIntensity: 1.25, ambientLift: 0.60,
    lightContrast: 0.65, shadeTint: 0.80, shadowStrength: 0.75, formLight: 0.70, macroVar: 0.50,
    bloomEnabled: true, bloomThreshold: 0.66, bloomIntensity: 1.00,
    gradeEnabled: true, gradeSplit: 1.50, gradeContrast: 0.36, gradeSat: 0.22,
    gradeVibrance: 0.40, gradeGreenTame: 0.35, gradeHighlight: 0.45,
  },
  dreamy: {
    label: '몽환', sky: { at: 'morning', cloud: 0.45 }, fogEnabled: true, fogAutoFar: false, fogNear: 40, fogFar: 420, shadowsEnabled: true, ssaoEnabled: false, terrainCastShadow: false, waterQuality: 2,
    shadowDistance: 160, vignetteEnabled: true, cloudShadowEnabled: true,
    toneMapping: true, toneExposure: 1.40, sunIntensity: 1.10, ambientLift: 0.95,
    lightContrast: 0.35, shadeTint: 0.65, shadowStrength: 0.55, formLight: 0.45, macroVar: 0.50,
    bloomEnabled: true, bloomThreshold: 0.60, bloomIntensity: 1.30,
    gradeEnabled: true, gradeSplit: 0.70, gradeContrast: 0.18, gradeSat: 0.14,
    gradeVibrance: 0.45, gradeGreenTame: 0.25, gradeHighlight: 0.35,
  },
  real: {
    label: '실사', sky: { at: 'late', cloud: 0.55 }, fogEnabled: true, fogAutoFar: false, fogNear: 120, fogFar: 1100, ...LOOK_HIGH, shadowDistance: 320, vignetteEnabled: true, cloudShadowEnabled: true,
    toneMapping: true, toneExposure: 0.82, sunIntensity: 1.15, ambientLift: 0.60,
    lightContrast: 0.55, shadeTint: 0.50, shadowStrength: 0.80, formLight: 0.70, macroVar: 0.65,
    bloomEnabled: true, bloomThreshold: 0.90, bloomIntensity: 0.40,
    gradeEnabled: true, gradeSplit: 0.50, gradeContrast: 0.28, gradeSat: -0.08,
    gradeVibrance: 0.12, gradeGreenTame: 0.55, gradeHighlight: 0.20,
  },
  vivid: {
    label: '선명', sky: { at: 'noon', cloud: 0.15 }, fogEnabled: true, fogAutoFar: true, fogNear: 600, shadowsEnabled: true, ssaoEnabled: true, terrainCastShadow: false, waterQuality: 2,
    shadowDistance: 200, vignetteEnabled: false, cloudShadowEnabled: true,
    toneMapping: true, toneExposure: 1.12, sunIntensity: 1.35, ambientLift: 0.80,
    lightContrast: 0.55, shadeTint: 0.45, shadowStrength: 0.62, formLight: 0.55, macroVar: 0.45,
    bloomEnabled: true, bloomThreshold: 0.85, bloomIntensity: 0.70,
    gradeEnabled: true, gradeSplit: 0.60, gradeContrast: 0.40, gradeSat: 0.30,
    gradeVibrance: 0.45, gradeGreenTame: 0.15, gradeHighlight: 0.25,
  },
  lite: {
    label: '가벼움', fogEnabled: true, fogAutoFar: true, fogNear: 350, shadowsEnabled: true, ssaoEnabled: false, terrainCastShadow: false, waterQuality: 1,
    shadowDistance: 100, vignetteEnabled: false, cloudShadowEnabled: false,
    ...Object.fromEntries(Object.entries(LIGHT_PRESETS.storybook).filter(([k]) => k !== 'label' && k !== 'desc')),
    bloomEnabled: false,
  },
};

// 빛 룩(v3 — 해↑·환경광↓·블룸 문턱↑로 면 대비를 세운다)을 이 브라우저에 **한 번만** 적용 — 룩은 조합이라 storybook 한 벌을 통째로 덮는다.
// 플래그를 남기는 이유 — 매 접속마다 덮으면 설정이 아니라 강제가 된다.
// ⚠ **로컬·DB 두 경로에서 모두 부른다** — 계정 유저는 로컬 뒤에 DB 가 덮으므로 한쪽만 걸면 안 먹는다.
export function applyLightLookV2(merged) {
  if (merged.lightLookV3 === true) return merged;
  merged.lightLookV3 = true;
  const p = LIGHT_PRESETS.storybook;
  for (const k of Object.keys(p)) { if (k !== 'label' && k !== 'desc') merged[k] = p[k]; }
  return merged;
}

// ── 성능 키는 기기에 산다 ─────────────────────────────────────
// 이 키들만 DB 동기화에서 빼 localStorage 에만 둔다 — 렌더타겟·메모리가 느는 축이라
// PC 에서 켠 값이 폰으로 따라가면 정확히 폰이 죽던 경로다(안드로이드 context-lost).
// 나머지(UI 배치·사운드·하늘·룩)는 「기기가 바뀌어도 뜻이 같은가」를 통과하므로 계정을 따른다.
// 어느 키가 기기 로컬인지는 스키마 행의 deviceLocal 이 정한다.
const DEVICE_LOCAL_KEYS = deviceLocalKeysOf(GAME_SETTINGS_SCHEMA);

// 기능과 함께 폐기된 키 — 저장본에서 턴다(loadLocal). 목록은 settingsSchema.RETIRED_SETTING_KEYS.
const RETIRED_KEYS = RETIRED_SETTING_KEYS;

// 원경 지형 기본 ON 승격 — 기본값만 바꾸면 저장된 0 이 이기므로 1회성 승격을 쓴다.
//   플래그를 세운 뒤로는 유저가 0 으로 되돌려도 영구히 존중된다.
// ⚠ PROFILE_CAPS 에 넣지 않는다 — 그 표는 «처음 켠 기기의 기본값»이라 유저 값을
//   덮어쓰므로, 넣으면 슬라이더를 움직여도 매번 되돌아간다. 기기 구분은 여기서만 한다.
const FAR_STEP_BY_PROFILE = {
  'pc-high': 16, 'pc-low': 16, 'mobile-standard': 32, 'mobile-low': 32,
};
export function applyFarTerrainV1(merged, profile) {
  if (merged.farTerrainV1 === true) return merged;
  merged.farTerrainV1 = true;
  if (merged.farTerrainStep === 0) merged.farTerrainStep = FAR_STEP_BY_PROFILE[profile] ?? 16;
  return merged;
}

// 원경 지형 2차 승격 — 원경은 근경의 짝이다(근경 파티션 반경이 원경 유무로 1 ↔ 3 으로 갈린다).
// ⚠ 이 승격 뒤에 끄면 반경이 3 으로 돌아가고, V2 도장이 찍혔으므로 다시는 안 켜진다.
// ⚠ 도장 키는 반드시 DEFAULT_SETTINGS 에 있어야 한다 — 없으면 DB 병합에서 사라져 매번 다시 돈다.
export function applyFarTerrainV2(merged, profile) {
  if (merged.farTerrainV2 === true) return merged;
  merged.farTerrainV2 = true;
  if (merged.farTerrainStep === 0) merged.farTerrainStep = FAR_STEP_BY_PROFILE[profile] ?? 16;
  return merged;
}

// 일회성 — 원경 도시·나무를 **한 번** 끈다. 기본값 하향만으로는 이미 `true` 로 저장된
// 브라우저에 안 닿는다. 한 번 false 로 내리고 도장을 찍는다 — 그 뒤 다시 켜면 그 선택이 남는다.
export function applyFarCityOffV1(merged) {
  if (merged.farCityOffV1 === true) return merged;
  merged.farCityOffV1 = true;
  merged.farCity = false;
  return merged;
}

// 산그늘(terrainCastShadow) 을 pc-high 에서만 한 번 켠다.
// ⚠ 기본값 하향/상향만으로는 이미 `false` 가 저장된 브라우저에 안 닿는다 — 1회성 승격이 필요하다.
//   승격 뒤에 유저가 끄면 도장이 남아 있으므로 다시는 안 켜진다(그 선택이 이긴다).
export function applyTerrainCastShadowV1(merged, profile) {
  if (merged.terrainCastShadowV1 === true) return merged;
  merged.terrainCastShadowV1 = true;
  if (profile === 'pc-high') merged.terrainCastShadow = true;
  return merged;
}

// 폰 근경 해상도 승격 — mobile-standard 에 저장된 옛 기본값 1.0 만 한 번 PROFILE_CAPS 값으로 올린다.
//   다른 값은 유저가 고른 것이라 안 건드린다. 도장 뒤에 유저가 내리면 그 선택이 이긴다.
//   근거: docs/task/2026-10-19/2340_모바일_근경화질_LOD거리_교환.md
export function applyMobileDprV1(merged, profile) {
  if (merged.mobileDprV1 === true) return merged;
  merged.mobileDprV1 = true;
  if (profile === 'mobile-standard' && merged.dprScale === 1.0) merged.dprScale = DEVICE_CAPS.dprScale;
  return merged;
}

// 화사한 지면(mc 화풍) 승격 — 초록 채도를 깎던 옛 값(0.55~0.85)을 한 번만 0.3 이하로.
//   도장(greenTameV1) 뒤에 유저가 올리면 그 선택이 이긴다.
// 고사양 그림자 승격 — pc-high 에서 한 번만 그림자 반경 160→240m · 접지 그늘(N8AO) 켬.
//   맵은 deviceProfile 에서 4096 이라 240m 에서도 텍셀이 약 0.12m 로 종전(0.10m)과 비슷하다.
//   도장 뒤에 유저가 내리면 그 선택이 이긴다.
export function applyShadowHiV1(merged, profile) {
  if (merged.shadowHiV1 === true) return merged;
  merged.shadowHiV1 = true;
  if (profile !== 'pc-high') return merged;
  if (typeof merged.shadowDistance !== 'number' || merged.shadowDistance < 240) merged.shadowDistance = 240;
  merged.ssaoEnabled = true;
  return merged;
}

export function applyGreenTameV1(merged) {
  if (merged.greenTameV1 === true) return merged;
  merged.greenTameV1 = true;
  if (typeof merged.gradeGreenTame === 'number') merged.gradeGreenTame = Math.min(merged.gradeGreenTame, 0.3);
  return merged;
}

const stripDeviceKeys = (s) => {
  const out = { ...s };
  for (const k of DEVICE_LOCAL_KEYS) delete out[k];
  return out;
};

/**
 * 끌어 옮긴 좌표를 저장하는 키들 — 위치는 계정에 저장돼 기기를 넘나든다.
 *
 * 이름표를 여기 같이 둔다: 환경설정의 「옮긴 위치 되돌리기」 칩 목록이 이 배열이다.
 * 「이 좌표를 되돌릴 수 있나」의 답이 여기 하나라, 새 패널은 한 줄만 늘리면 된다.
 *
 * 세 번째 칸 = 높이 키. 없는 줄은 세로 크기를 못 바꾸는 판이다.
 * ⚠ `POS_KEYS` 는 첫 칸만 쓰므로 세 번째 칸을 늘려도 clamp 는 안 깨진다.
 */
export const POS_PANELS = [
  ['hudPosGold', '골드 칩'],
  ['hudPosSpeed', 'SPD'],
  ['hudPosClock', '시계'],
  ['panelPosQuestTracker', '퀘스트 카드'],
  ['panelPosQuest', '퀘스트 목록', 'panelHQuest'],
  ['panelPosQuestDetail', '퀘스트 상세'],
  ['hudPosMusic', '배경음 아이콘'],
  ['hudPosStealth', '흑백 모드 아이콘'],
  ['panelPosMusic', '배경음 창'],
  ['panelPosMusicFilter', '배경음 필터'],
  ['panelPosCraft', '대장간'],
  ['panelPosInventory', '인벤토리'],
  ['panelPosSkillPanel', '스킬 목록', 'panelHSkillPanel'],
  ['panelPosSettings', '환경설정', 'panelHSettings'],
  ['panelPosKeybind', '단축키', 'panelHKeybind'],
  ['panelPosChat', '채팅'],
  ['panelPosPetPanel', '펫'],
  ['panelPosPetDetail', '펫 상세'],
  ['panelPosShopPanel', '보물고·캐시샵'],
  ['panelPosAuctionPanel', '경매장'],
  ['panelPosMercenaryPanel', '용병 고용소'],
  ['panelPosMercenaryWatch', '원정 관전'],
  ['panelPosMercenaryDetail', '용병 상세 정보'],
  ['panelPosGearFitting', '장비 시연장'],
  ['panelPosSocial', '소셜', 'panelHSocial'],
  ['panelPosEstate', '부동산 관리'],
  ['panelPosEstateInfo', '부동산 정보'],
  ['panelPosQuestCard', '문답 부탁'],
  ['panelPosVillainRequest', '빌런 의뢰'],
  ['panelPosStats', '스탯창'],
  ['panelPosCodex', '도감'],
  ['panelPosHelpPanel', '길잡이'],
  ['panelPosQuestPanel', '퀘스트'],
  ['panelPosAllFeatures', '전체 기능'],
  ['panelPosFeed', '블록톡'],
  ['panelPosVoice', '장터말'],
  ['panelPosLayers', '레이어'],
  ['panelPosBuildMode', '빌드모드'],
  ['panelPosWarp', '워프 게이트'],
  ['panelPosTransit', '교통 현황'],
];
const POS_KEYS = POS_PANELS.map(([k]) => k);
/** 세로 크기 조절이 붙은 판의 높이 키만. clamp 와 되돌리기 칩이 같이 본다. */
const PANEL_H_KEYS = POS_PANELS.map(([, , h]) => h).filter(Boolean);

/**
 * 화면 밖으로 나간 좌표를 **화면 안으로 끌어온다**(제자리 수정).
 *
 * ⚠ 지우지 않고 옮긴다. `null` 로 지우면 다음 저장에 DB 에도 null 이 박혀 영구 소실된다.
 * 로컬·DB 양쪽 경로에서 부른다 — 한쪽만 걸면 다른 쪽에서 온 화면 밖 좌표가 그대로 그려진다.
 */
function clampPanelPositions(s) {
  const W = (typeof window !== 'undefined' && window.innerWidth) || 0;
  const H = (typeof window !== 'undefined' && window.innerHeight) || 0;
  const MARGIN = 40;   // 이만큼은 화면 안에 남아야 잡아서 다시 끌 수 있다
  for (const key of POS_KEYS) {
    const p = s[key];
    if (!p) continue;
    if (typeof p.x !== 'number' || typeof p.y !== 'number'
        || !Number.isFinite(p.x) || !Number.isFinite(p.y)) {
      s[key] = null;                 // 좌표가 아닌 것만 버린다
      continue;
    }
    if (!W || !H) continue;          // SSR/테스트 — 잴 화면이 없으면 손대지 않는다
    const x = Math.max(0, Math.min(p.x, W - MARGIN));
    const y = Math.max(0, Math.min(p.y, H - MARGIN));
    if (x !== p.x || y !== p.y) s[key] = { x, y };
  }
  clampPanelHeights(s, H);
  return s;
}

/**
 * 저장된 패널 높이를 이 화면에 맞게 끌어내린다.
 *
 * 큰 화면에서 늘린 판이 작은 화면에서는 밑변이 화면 밖이라 그립을 다시 못 잡는다.
 * 좌표와 마찬가지로 **지우지 않고 줄인다** — 지우면 큰 화면으로 돌아가도 안 살아난다.
 */
function clampPanelHeights(s, H) {
  const h = H || (typeof window !== 'undefined' && window.innerHeight) || 0;
  for (const key of PANEL_H_KEYS) {
    const v = s[key];
    if (v == null) continue;
    if (typeof v !== 'number' || !Number.isFinite(v)) { s[key] = null; continue; }
    if (!h) continue;                       // SSR/테스트 — 잴 화면이 없으면 손대지 않는다
    const max = Math.max(120, h - 24);      // PANEL_MIN_H(120) 와 같은 하한
    const next = Math.max(120, Math.min(v, max));
    if (next !== v) s[key] = next;
  }
  return s;
}

function loadLocal() {
  try {
    const raw = getRaw(STORAGE_KEY);
    // ⚠ 새 브라우저는 여기서 끝난다 — 아래 보정·승격을 **한 줄도 안 탄다.**
    //   그래서 「기기가 정하는 기본값」은 이 갈래에도 따로 걸어야 한다. 나머지 승격은
    //   전부 DEFAULT_SETTINGS 가 이미 정답이라 안 걸어도 된다(이것만 프로필에 갈린다).
    if (!raw) return applyMobileDprV1(applyShadowHiV1(applyTerrainCastShadowV1({ ...DEFAULT_SETTINGS }, DEVICE_PROFILE), DEVICE_PROFILE), DEVICE_PROFILE);
    const merged = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    if (typeof merged.cameraFov !== 'number' || merged.cameraFov < 10 || merged.cameraFov > 100) {
      merged.cameraFov = DEFAULT_SETTINGS.cameraFov;
    }
    if (typeof merged.cameraPitch !== 'number' || merged.cameraPitch < 30 || merged.cameraPitch > 75) {
      merged.cameraPitch = DEFAULT_SETTINGS.cameraPitch;
    }
    if (typeof merged.cameraTargetLift !== 'number' || merged.cameraTargetLift < 0 || merged.cameraTargetLift > 15) {
      merged.cameraTargetLift = DEFAULT_SETTINGS.cameraTargetLift;
    }
    if (typeof merged.cameraShakeStrength !== 'number' || merged.cameraShakeStrength < 0 || merged.cameraShakeStrength > 1.5) {
      merged.cameraShakeStrength = DEFAULT_SETTINGS.cameraShakeStrength;
    }
    // HDRI 프리셋 마이그레이션 — 구버전 키(sky/night/sunset) → 신규 키
    merged.hdriPreset = resolveHdriPreset(merged.hdriPreset);
    merged.skyMode = 'live';
    if (typeof merged.bloomThreshold !== 'number' || merged.bloomThreshold < 0.5 || merged.bloomThreshold > 1.0)
      merged.bloomThreshold = DEFAULT_SETTINGS.bloomThreshold;
    if (typeof merged.bloomIntensity !== 'number' || merged.bloomIntensity < 0 || merged.bloomIntensity > 2)
      merged.bloomIntensity = DEFAULT_SETTINGS.bloomIntensity;
    // 퀵슬롯 범위 가드 — 개수는 정수 1~6(HOTBAR_KEYS 길이), 배율은 0.7~1.5
    // 자동 회복 문턱 — 0(끔) 또는 10~80. 90% 같은 값은 물약을 끝없이 태우므로 위도 막는다.
    for (const k of ['autoPotionPct', 'autoPotionMpPct']) {
      if (typeof merged[k] !== 'number' || merged[k] < 0 || merged[k] > 80 || (merged[k] > 0 && merged[k] < 10)) {
        merged[k] = DEFAULT_SETTINGS[k];
      }
    }
    merged.autoPotionEnabled = merged.autoPotionEnabled === true;
    if (typeof merged.attackReticle !== 'boolean') merged.attackReticle = DEFAULT_SETTINGS.attackReticle;
    for (const [k, lo, hi] of [['attackReticleScale', 0.75, 1.5], ['attackReticleAlpha', 0.2, 0.8]]) {
      if (typeof merged[k] !== 'number' || merged[k] < lo || merged[k] > hi) merged[k] = DEFAULT_SETTINGS[k];
    }
    if (typeof merged.multiDongCap !== 'number' || merged.multiDongCap < 1 || merged.multiDongCap > 5) {
      merged.multiDongCap = DEFAULT_SETTINGS.multiDongCap;
    }
    if (typeof merged.dprScale !== 'number' || merged.dprScale < 0.5 || merged.dprScale > 1.5) {
      merged.dprScale = DEFAULT_SETTINGS.dprScale;
    }
    if (typeof merged.shadowsEnabled !== 'boolean') {
      merged.shadowsEnabled = DEFAULT_SETTINGS.shadowsEnabled;
    }
    // 빛·그림자 — 범위 밖·타입 불일치는 조용히 기본값으로(저장값이 없으면 DEFAULT 병합이 처리).
    for (const [k, lo, hi] of [
      ['toneExposure', 0.5, 1.8], ['sunIntensity', 0.5, 2.0], ['ambientLift', 0.4, 1.8],
      ['shadowStrength', 0.1, 1.0], ['shadowDistance', 80, 400], ['windStrength', 0, 2.5],
      ['propScale', 0.6, 3.0],
    ]) {
      if (typeof merged[k] !== 'number' || !Number.isFinite(merged[k]) || merged[k] < lo || merged[k] > hi) {
        merged[k] = DEFAULT_SETTINGS[k];
      }
    }
    for (const k of ['toneMapping', 'groundShadow', 'terrainCastShadow']) {
      if (typeof merged[k] !== 'boolean') merged[k] = DEFAULT_SETTINGS[k];
    }
    if (!Number.isInteger(merged.invCols) || merged.invCols < 4 || merged.invCols > 8) merged.invCols = DEFAULT_SETTINGS.invCols;
    if (!Number.isInteger(merged.invRows) || merged.invRows < 4 || merged.invRows > 12) merged.invRows = DEFAULT_SETTINGS.invRows;
    // 레일 기둥 길이(세로 칸 수) — null(자동)이거나 1~14 정수만. 그 밖은 자동.
    if (merged.hudRailRows != null
        && (!Number.isInteger(merged.hudRailRows) || merged.hudRailRows < 1 || merged.hudRailRows > 14)) {
      merged.hudRailRows = null;
    }
    clampPanelPositions(merged);
    // 폐기된 키를 저장본에서 턴다 — 안 읽히더라도 남아 있으면 계정을 따라 영원히 실려 다니고,
    // 설정 화면을 열 때마다 「이건 뭐지」의 후보가 된다. 지운 기능의 값은 같이 지운다.
    // ⚠ 메모리에서만 지우면 파일에 그대로 남는다 — 아래에서 되쓸 수 있게 여부를 든다.
    const retiredFound = RETIRED_KEYS.some((k) => k in merged);
    for (const k of RETIRED_KEYS) delete merged[k];
    // 빛 룩 v2 — 기존 유저에게 한 번만 적용(applyLightLookV2 머리말 참조).
    applyLightLookV2(merged);
    // 승격이 실제로 일어났는지 — 아래에서 localStorage 로 되쓴다. 메모리에만 세우면 유저가
    // 아무것도 저장하지 않고 나갔을 때 도장이 안 남아 다음 접속에 또 승격한다.
    const farPromoted = merged.farTerrainV1 !== true || merged.farTerrainV2 !== true
      || merged.terrainCastShadowV1 !== true || merged.greenTameV1 !== true || merged.shadowHiV1 !== true;
    applyFarTerrainV1(merged, DEVICE_PROFILE);
    applyFarTerrainV2(merged, DEVICE_PROFILE);
    applyFarCityOffV1(merged);
    applyTerrainCastShadowV1(merged, DEVICE_PROFILE);
    applyGreenTameV1(merged);
    applyShadowHiV1(merged, DEVICE_PROFILE);
    applyMobileDprV1(merged, DEVICE_PROFILE);
    if (typeof merged.fogEnabled !== 'boolean') merged.fogEnabled = DEFAULT_SETTINGS.fogEnabled;
    if (typeof merged.fogNear !== 'number' || merged.fogNear < 30 || merged.fogNear > 6000) {
      merged.fogNear = DEFAULT_SETTINGS.fogNear;
    }
    if (![0, 4, 8, 16, 32].includes(merged.farTerrainStep)) {
      merged.farTerrainStep = DEFAULT_SETTINGS.farTerrainStep;
    }
    if (typeof merged.farTerrainFar !== 'number'
        || merged.farTerrainFar < 2000 || merged.farTerrainFar > 12000) {
      merged.farTerrainFar = DEFAULT_SETTINGS.farTerrainFar;
    }
    if (typeof merged.farCity !== 'boolean') merged.farCity = DEFAULT_SETTINGS.farCity;
    if (typeof merged.farTerrainLights !== 'number'
        || merged.farTerrainLights < 0 || merged.farTerrainLights > 1.5) {
      merged.farTerrainLights = DEFAULT_SETTINGS.farTerrainLights;
    }
    if (typeof merged.fogFar !== 'number' || merged.fogFar < 200 || merged.fogFar > 12000) {
      merged.fogFar = DEFAULT_SETTINGS.fogFar;
    }
    if (merged.fogFar < merged.fogNear + 50) merged.fogFar = merged.fogNear + 50; // near/far 역전 방지
    if (typeof merged.cloudShadowEnabled !== 'boolean') {
      merged.cloudShadowEnabled = DEFAULT_SETTINGS.cloudShadowEnabled;
    }
    if (typeof merged.cloudShadowStrength !== 'number' || merged.cloudShadowStrength < 0 || merged.cloudShadowStrength > 0.8) {
      merged.cloudShadowStrength = DEFAULT_SETTINGS.cloudShadowStrength;
    }
    if (!Number.isInteger(merged.cloudQuality) || merged.cloudQuality < 0 || merged.cloudQuality > 2) {
      merged.cloudQuality = DEFAULT_SETTINGS.cloudQuality;
    }
    // 원경 승격 도장을 **그 자리에서** 굳힌다 (위 farPromoted 참조).
    // 폐기 키를 턴 판도 같이 굳힌다 — 안 그러면 지운 값이 파일에 남아 매 접속 되살아난다.
    if (farPromoted || retiredFound) {
      saveJSON(STORAGE_KEY, merged);
    }
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export const useGameSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(loadLocal);
  const dbSyncedRef = useRef(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;   // saveToDb 가 업데이터 밖에서 최신값을 읽기 위한 미러

  // 로그인 유저: 최초 1회 DB에서 로드 (DB > localStorage > 기본값)
  useEffect(() => {
    if (!user?.id || dbSyncedRef.current) return;
    dbSyncedRef.current = true;
    gameApi.getSettings(user.id)
      .then(res => {
        const dbSettings = res.data?.settings;
        if (dbSettings && Object.keys(dbSettings).length > 0) {
          // 성능 키는 DB 값을 **안 받는다** — 다른 기기(=대개 PC)가 올려 둔 값이
          // 이 기기의 판단을 덮으면 안 된다. 이 브라우저의 현재 값을 그대로 둔다.
          const merged = { ...DEFAULT_SETTINGS, ...stripDeviceKeys(dbSettings) };
          for (const k of RETIRED_KEYS) delete merged[k];
          for (const k of DEVICE_LOCAL_KEYS) merged[k] = settingsRef.current[k];
          merged.hdriPreset = resolveHdriPreset(merged.hdriPreset); // 구버전 키 정규화
          merged.skyMode = 'live'; // 정적 HDRI 폐기 — DB에 남은 구값도 강제 마이그레이션
          // 룩 마이그레이션 플래그는 **이 브라우저 것**을 살린다. DB 에는 [저장] 을 눌러야
          // 올라가므로 DB 값만 믿으면, 저장을 안 누른 유저는 접속할 때마다 룩이 덮어써진다.
          merged.lightLookV3 = settingsRef.current.lightLookV3 === true || dbSettings.lightLookV3 === true;
          applyLightLookV2(merged);
          // ⚠ 원경 승격 도장도 **이 브라우저 것을 살린다** — 값(farTerrainStep)은 성능 키라
          //   DB 에 안 올라가므로, 도장만 DB 를 따라가면 매 접속마다 승격이 다시 돈다.
          //   성능 키와 그 승격 도장은 같은 저장소에 살아야 한다.
          merged.farTerrainV1 = settingsRef.current.farTerrainV1 === true || dbSettings.farTerrainV1 === true;
          merged.farTerrainV2 = settingsRef.current.farTerrainV2 === true || dbSettings.farTerrainV2 === true;
          clampPanelPositions(merged); // 다른 기기에서 옮긴 좌표가 이 화면 밖일 수 있다
          setSettings(merged);
          saveJSON(STORAGE_KEY, merged);
        }
      })
      .catch(() => {}); // DB 실패 시 localStorage 유지
  }, [user?.id]);

  // 자동 DB 동기화 — 끌어서 옮긴 좌표(hudPos*·panelPos*)는 [저장] 버튼(saveToDb)을 안 거친다.
  // 디바운스 1.5초(슬라이더는 끄는 동안 수십 번 부른다) · 토스트는 안 띄우고 실패도 삼킨다.
  const syncTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(syncTimerRef.current), []);
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      saveJSON(STORAGE_KEY, next);
      return next;
    });
    if (!user?.id) return;
    clearTimeout(syncTimerRef.current);
    // settingsRef 는 렌더마다 갱신되므로 1.5초 뒤엔 방금 바꾼 값이 들어 있다.
    syncTimerRef.current = setTimeout(() => {
      gameApi.saveSettings(user.id, stripDeviceKeys(settingsRef.current)).catch(() => {});
    }, 1500);
  }, [user?.id]);

  // DB 저장 (환경설정 [저장] 버튼) — 성공·실패 양쪽 다 토스트로 알린다(ui_rules.md §액션 피드백).
  //   업데이터 «밖»에서 쏜다. 안에서 쏘고 catch 로 삼키면 저장 성공과 서버 장애가 화면상 같아진다.
  const saveToDb = useCallback(async (overrides = {}) => {
    const next = { ...settingsRef.current, ...overrides };
    setSettings(next);
    saveJSON(STORAGE_KEY, next);
    if (!user?.id) {
      toast.ok('설정을 저장했습니다', { detail: '이 브라우저에만' });
      return false;
    }
    try {
      await gameApi.saveSettings(user.id, stripDeviceKeys(next));
      toast.ok('설정을 저장했습니다');
      return true;
    } catch {
      toast.err('설정 저장 실패', { detail: '서버에 반영되지 않았습니다 · 잠시 후 다시 시도' });
      return false;
    }
  }, [user?.id]);

  const resetSettings = useCallback(async () => {
    removeKey(STORAGE_KEY);
    setSettings({ ...DEFAULT_SETTINGS });
    if (!user?.id) {
      toast.ok('기본값으로 초기화했습니다');
      return;
    }
    try {
      await gameApi.saveSettings(user.id, stripDeviceKeys(DEFAULT_SETTINGS));
      toast.ok('기본값으로 초기화했습니다');
    } catch {
      toast.err('초기화 저장 실패', { detail: '화면만 초기화됨 · 서버 미반영' });
    }
  }, [user?.id]);

  // 소비처와 설정 화면이 **같은 물건**을 본다. rawSettings 는 부르는 쪽 호환으로만 남긴다.
  return { settings, rawSettings: settings, updateSetting, saveToDb, resetSettings };
};
